import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress, Alert,
  Divider, Stack, TextField, Chip,
} from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ArchiveIcon from '@mui/icons-material/Archive';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────
interface RetrievalEstimate {
  messages: { mailId: string; subject: string; blobTier: string; sizeBytes: number }[];
  coolCount: number;
  archiveCount: number;
  coolBaseFee: number;
  coolSizeFee: number;
  archiveBaseFee: number;
  archiveSizeFee: number;
  totalEstimatedFee: number;
  userAvailableBalance: number;
  hasSufficientFunds: boolean;
}

interface RetrievalResult {
  success: boolean;
  actualFee: number;
  receiptMailId?: string;
  retrievedCount: number;
  downloadNotice: string;
}

interface RetrievalModalProps {
  open: boolean;
  onClose: () => void;
  /** Single-message mode: provide mailId */
  mailId?: string;
  subject?: string;
  blobTier?: string;
  blobSizeBytes?: number | null;
  /** After successful retrieval, proceed to open the message */
  onRetrieved?: (mailId: string) => void;
  /** On close after success, refresh the message list */
  onListRefresh?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────
const RetrievalModal: React.FC<RetrievalModalProps> = ({
  open, onClose, mailId, subject, blobTier, onRetrieved, onListRefresh,
}) => {
  const [mode, setMode] = useState<'single' | 'range'>('single');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [estimate, setEstimate] = useState<RetrievalEstimate | null>(null);
  const [loading, setLoading]   = useState(false);
  const [retrieving, setRetrieving] = useState(false);
  const [result, setResult]     = useState<RetrievalResult | null>(null);
  const [error, setError]       = useState<string | null>(null);

  // Auto-estimate when modal opens in single-message mode
  useEffect(() => {
    if (open && mailId && mode === 'single') {
      fetchEstimate();
    }
  }, [open, mailId]);

  const fetchEstimate = async () => {
    setLoading(true);
    setError(null);
    setEstimate(null);
    try {
      const params = mode === 'single' && mailId
        ? `?mailIds=${mailId}`
        : `?dateFrom=${dateFrom}&dateTo=${dateTo}`;
      const resp = await api.get(`/retrieval/estimate${params}`) as { data: RetrievalEstimate };
      setEstimate(resp.data);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to load estimate.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetrieve = async () => {
    if (!estimate) return;
    setRetrieving(true);
    setError(null);
    try {
      const body = mode === 'single' && mailId
        ? { mailIds: [mailId] }
        : { dateFrom, dateTo };
      const resp = await api.post('/retrieval/retrieve', body) as { data: RetrievalResult };
      setResult(resp.data);
      onListRefresh?.();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Retrieval failed — please try again.');
    } finally {
      setRetrieving(false);
    }
  };

  const handleOpenMessage = () => {
    if (mailId && onRetrieved) onRetrieved(mailId);
    onClose();
  };

  const handleClose = () => {
    setEstimate(null);
    setResult(null);
    setError(null);
    setLoading(false);
    setRetrieving(false);
    onClose();
  };

  const tierColor = (tier: string) => tier === 'COOL' ? '#1565c0' : '#880e4f';
  const tierIcon  = (tier: string) => tier === 'COOL'
    ? <AcUnitIcon sx={{ fontSize: 16 }} />
    : <ArchiveIcon sx={{ fontSize: 16 }} />;

  // ── Render success state ───────────────────────────────────────────────────
  if (result?.success) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          Messages Retrieved
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            {result.retrievedCount} message{result.retrievedCount !== 1 ? 's' : ''} moved to Hot storage.
            ${result.actualFee.toFixed(2)} charged to your account.
            {result.receiptMailId && ' A receipt has been sent to your Debits folder.'}
          </Alert>

          <Box sx={{ p: 2, bgcolor: '#fff8e1', borderLeft: '4px solid #ffc107', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <DownloadIcon sx={{ color: '#f57c00', mt: 0.3 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="#e65100">
                  Avoid future retrieval fees
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Download messages to your device in an unencrypted format to maintain a permanent
                  local archive. Once saved locally, you can delete the server copy and access
                  your messages for free — forever.
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5} fontStyle="italic">
                  Unencrypted download coming in Phase 3.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          {mailId && onRetrieved && (
            <Button variant="contained" onClick={handleOpenMessage}>
              Open Message
            </Button>
          )}
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Render main modal ──────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Retrieve{blobTier ? ` ${blobTier === 'COOL' ? '❄️ Cool' : '📦 Archive'}` : ''} Storage Message
      </DialogTitle>

      <DialogContent>
        {/* Single vs date-range tabs */}
        <Stack direction="row" spacing={1} mb={2}>
          <Chip
            label="Single message"
            variant={mode === 'single' ? 'filled' : 'outlined'}
            color="primary"
            clickable
            onClick={() => { setMode('single'); setEstimate(null); }}
          />
          <Chip
            label="Date range"
            variant={mode === 'range' ? 'filled' : 'outlined'}
            color="primary"
            clickable
            onClick={() => { setMode('range'); setEstimate(null); }}
          />
        </Stack>

        {mode === 'single' && subject && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">Message:</Typography>
            <Typography variant="body1" fontWeight={500}>{subject}</Typography>
            {blobTier && (
              <Chip
                size="small"
                icon={tierIcon(blobTier)}
                label={blobTier === 'COOL' ? 'Cool storage' : 'Archive storage'}
                sx={{ mt: 0.5, color: tierColor(blobTier) }}
              />
            )}
          </Box>
        )}

        {mode === 'range' && (
          <Stack spacing={2} mb={2}>
            <TextField
              label="From date"
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="To date"
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Button variant="outlined" onClick={fetchEstimate} disabled={!dateFrom || !dateTo || loading}>
              {loading ? <CircularProgress size={20} /> : 'Calculate cost'}
            </Button>
          </Stack>
        )}

        {/* Pricing context */}
        <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Retrieval pricing:</strong>
            {' '}❄️ Cool: $2.00 base + $0.10/MB&nbsp;&nbsp;|&nbsp;&nbsp;
            📦 Archive: $7.00 base + $0.20/MB
          </Typography>
        </Box>

        {/* Loading */}
        {loading && <Box textAlign="center" py={2}><CircularProgress size={32} /></Box>}

        {/* Error */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Estimate */}
        {estimate && (
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Cost Estimate</Typography>

            {estimate.coolCount > 0 && (
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">
                  ❄️ Cool ({estimate.coolCount} msg{estimate.coolCount !== 1 ? 's' : ''})
                </Typography>
                <Typography variant="body2">
                  ${estimate.coolBaseFee.toFixed(2)} + ${estimate.coolSizeFee.toFixed(4)}/MB
                </Typography>
              </Stack>
            )}
            {estimate.archiveCount > 0 && (
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">
                  📦 Archive ({estimate.archiveCount} msg{estimate.archiveCount !== 1 ? 's' : ''})
                </Typography>
                <Typography variant="body2">
                  ${estimate.archiveBaseFee.toFixed(2)} + ${estimate.archiveSizeFee.toFixed(4)}/MB
                </Typography>
              </Stack>
            )}

            <Divider sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight={700}>Total</Typography>
              <Typography fontWeight={700}>${estimate.totalEstimatedFee.toFixed(2)}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Your balance: ${estimate.userAvailableBalance.toFixed(2)}
            </Typography>

            {!estimate.hasSufficientFunds && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Insufficient balance. You need ${estimate.totalEstimatedFee.toFixed(2)} but have $
                {estimate.userAvailableBalance.toFixed(2)}.
              </Alert>
            )}

            {estimate.messages.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No Cool or Archive messages found for this selection.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={retrieving}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRetrieve}
          disabled={
            retrieving || loading || !estimate ||
            !estimate.hasSufficientFunds ||
            estimate.messages.length === 0
          }
          startIcon={retrieving ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {retrieving ? 'Retrieving…' : `Retrieve — $${estimate?.totalEstimatedFee.toFixed(2) ?? '…'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RetrievalModal;
